import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import { isReachableImage } from '../utils/isReachableImage';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { getFallbackCountryImageUrl, optimizeCountryImageUrl } from '../utils/countryImages';

type ImageMap = Record<string, string>;

interface CountryImagesContextValue {
  ready: boolean;
  getImage: (countryCode?: string | null) => string;
  getImages: (countryCode?: string | null) => string[];
  hydrateFromCountries: (
    rows: Array<{ countryCode?: string | null; backgroundImage?: string | null }>,
  ) => void;
  refresh: () => Promise<void>;
}

const CountryImagesContext = createContext<CountryImagesContextValue>({
  ready: false,
  getImage: (code) => getFallbackCountryImageUrl(code || ''),
  getImages: (code) => [getFallbackCountryImageUrl(code || '')],
  hydrateFromCountries: () => undefined,
  refresh: async () => undefined,
});

/**
 * Priority:
 * 1. Admin / CDN banners (R2) that pass the URL allow-list
 * 2. Generic travel photo — never Nest `/static/`, never Wikipedia
 */
export function CountryImagesProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<ImageMap>({});
  const [banners, setBanners] = useState<Record<string, string[]>>({});
  const [ready, setReady] = useState(false);
  const loadingRef = useRef<Promise<void> | null>(null);
  const verifiedRef = useRef<Set<string>>(new Set());

  const acceptAdminUrl = useCallback(async (code: string, url: string) => {
    const optimized = optimizeCountryImageUrl(url);
    if (verifiedRef.current.has(`${code}:${optimized}`)) {
      setMap((prev) => (prev[code] === optimized ? prev : { ...prev, [code]: optimized }));
      return;
    }
    const ok = await isReachableImage(optimized);
    if (!ok) return;
    verifiedRef.current.add(`${code}:${optimized}`);
    setMap((prev) => (prev[code] === optimized ? prev : { ...prev, [code]: optimized }));
  }, []);

  const refresh = useCallback(async () => {
    if (loadingRef.current) return loadingRef.current;

    const run = (async () => {
      try {
        const catalog = await api.getCountryImageCatalog();
        const nextBanners: Record<string, string[]> = {};

        await Promise.all(
          catalog.map(async (row) => {
            const code = String(row.countryCode || '').toUpperCase();
            if (!code) return;
            const urls = (row.bannerImages || [])
              .map((b) => resolveAssetUrl(b.imageUrl))
              .filter((u): u is string => Boolean(u));
            const hero =
              resolveAssetUrl(row.heroImageUrl) || resolveAssetUrl(row.backgroundImage) || null;
            const primary = hero || urls[0] || null;
            if (primary) await acceptAdminUrl(code, primary);
            const alive: string[] = [];
            for (const u of urls) {
              if (await isReachableImage(u)) alive.push(optimizeCountryImageUrl(u));
            }
            if (alive.length) {
              nextBanners[code] = alive;
            } else if (primary && (await isReachableImage(primary))) {
              nextBanners[code] = [optimizeCountryImageUrl(primary)];
            }
          }),
        );

        setBanners(nextBanners);
      } catch {
        /* keep fallbacks */
      } finally {
        setReady(true);
        loadingRef.current = null;
      }
    })();

    loadingRef.current = run;
    return run;
  }, [acceptAdminUrl]);

  const hydrateFromCountries = useCallback(
    (rows: Array<{ countryCode?: string | null; backgroundImage?: string | null }>) => {
      if (!rows?.length) return;
      setReady(true);
      for (const row of rows) {
        const code = String(row.countryCode || '').toUpperCase();
        const url = resolveAssetUrl(row.backgroundImage);
        if (!code || !url) continue;
        void acceptAdminUrl(code, url);
      }
    },
    [acceptAdminUrl],
  );

  const getImage = useCallback(
    (countryCode?: string | null) => {
      const code = String(countryCode || '').toUpperCase();
      if (map[code]) return map[code];

      if (!ready && !loadingRef.current) {
        void refresh();
      }

      return getFallbackCountryImageUrl(code);
    },
    [map, ready, refresh],
  );

  const getImages = useCallback(
    (countryCode?: string | null) => {
      const code = String(countryCode || '').toUpperCase();
      const list = banners[code];
      if (list?.length) return list;
      if (!ready && !loadingRef.current) {
        void refresh();
      }
      return [getImage(code)];
    },
    [banners, getImage, ready, refresh],
  );

  const value = useMemo(
    () => ({ ready, getImage, getImages, hydrateFromCountries, refresh }),
    [ready, getImage, getImages, hydrateFromCountries, refresh],
  );

  return <CountryImagesContext.Provider value={value}>{children}</CountryImagesContext.Provider>;
}

export function useCountryImages() {
  return useContext(CountryImagesContext);
}
